// Feature: M004-F0261 (admin user management page) — owner ruling 2026-10-04.
//
// Visibility rules and confirm wording for the two "take a person out" actions
// on /app/admin/users. Pure functions so the rules are unit-testable without
// mounting the page, and so the page and its tests share ONE definition.
//
//   1. Delete (archive) user  — gated by the SYSTEM capability `users_delete`.
//   2. Remove from organisation — gated by the ORGANISATION capability
//      `organisation_users_remove` (what Core's
//      DELETE /api/organisations/:id/users/:userId asserts), resolved per
//      organisation from GET /organisations/:id/my-permissions.
//
// Neither reads a role name. Both fail closed: anything other than a literal
// `true` hides the control.

export const USERS_DELETE_CAPABILITY = 'users_delete';
export const ORG_USERS_REMOVE_CAPABILITY = 'organisation_users_remove';

// The caller's OWN system capability map comes from Core's
// GET /api/users/me/system-permissions (userApi.getMySystemPermissions; Core
// MR !966, issue #280; needs SAL 3, no capability). The EXACT body is
//   { success: true, data: { role, systemPermissions: { users_delete: bool, ... } } }
// and nothing else is understood: any other shape yields {} and therefore no
// Delete. `GET /v1/admin/permissions/system` is deliberately not used: it
// needs permissions_system_manage and SAL-4.
export const SYSTEM_PERMISSIONS_FIELD = 'systemPermissions';
export const SYSTEM_CAPABILITY_FIELD_FOR_DELETE = USERS_DELETE_CAPABILITY;

export const extractSystemPermissionMap = (response) => {
  const map = response?.data?.data?.[SYSTEM_PERMISSIONS_FIELD];
  return map && typeof map === 'object' && !Array.isArray(map) ? map : {};
};

// Resolves to the caller's map, or {} on ANY failure (call rejected, missing
// client method, malformed body): the gate then fails closed.
export const loadMySystemPermissions = async (userApi) => {
  try {
    return extractSystemPermissionMap(await userApi.getMySystemPermissions());
  } catch {
    return {};
  }
};

export const canDeleteUsers = (systemPermissions) => (
  systemPermissions?.[SYSTEM_CAPABILITY_FIELD_FOR_DELETE] === true
);

// `permissions` is the body of my-permissions for ONE organisation.
export const canRemoveFromOrganisation = (permissions) => (
  permissions?.[ORG_USERS_REMOVE_CAPABILITY] === true
);

const ARCHIVE_FACTS = 'The account is archived (soft-deleted): its email address is renamed and its sessions are revoked. '
  + 'Door access in RealmAuthority is not changed by this.';

export const buildDeleteUserConfirm = (displayName) => (
  `Archive ${displayName}? ${ARCHIVE_FACTS}`
);

export const buildBulkDeleteUserConfirm = (count) => (
  `Archive ${count} selected user(s)? Each account is archived (soft-deleted): its email address is renamed and its sessions are revoked. `
  + 'Door access in RealmAuthority is not changed by this.'
);

export const buildRemoveFromOrganisationConfirm = (personName, organisationName, isPrimary = false) => (
  `Remove ${personName} from ${organisationName}? This removes their membership of that organisation only. `
  + 'It does not delete their account, does not remove their existing bookings or invoices, '
  + 'and does not change their door access.'
  + (isPrimary
    ? ' This is their primary organisation, so they will have no primary organisation until one is set.'
    : '')
);
